import React from 'react';
import { CheckCircle2, Layers, Rocket, ShieldCheck, Truck, Warehouse, FileText, Calculator, LineChart, ShoppingBag } from 'lucide-react';
import { PageHero, SectionTitle, Card } from '../components/PageUI';

const ErpPage = () => {
  const modules = [
    { title: 'Compras', icon: ShoppingBag, items: ['Solicitações e aprovações', 'Sugestão automática', 'Cotações e pedidos', 'Recebimento via XML'] },
    { title: 'Vendas', icon: Rocket, items: ['Orçamentos e pedidos', 'PDV e televendas', 'Tabelas de preço', 'Contratos e projetos'] },
    { title: 'Estoque (WMS)', icon: Warehouse, items: ['Lotes e endereçamento', 'Picking inteligente', 'Inventário rotativo', 'Múltiplos estoques'] },
    { title: 'Entregas (TMS)', icon: Truck, items: ['Geo-roteirização com IA', 'Montagem de cargas', 'GPS em tempo real', 'Prestação de contas'] },
    { title: 'Fiscal', icon: FileText, items: ['NF-e, NFC-e, MDF-e', 'SPED Fiscal', 'Sefaz', 'Notas de devolução'] },
    { title: 'Finanças', icon: Calculator, items: ['Conciliação automática', 'Fluxo de caixa', 'DRE customizável', 'Telecobrança'] },
    { title: 'Business Intelligence', icon: LineChart, items: ['Dashboards customizáveis', 'Relatórios dinâmicos', 'Níveis de acesso', 'Indicadores em tempo real'] },
    { title: 'Produção', icon: Layers, items: ['Fichas e ordens', 'Custos e rastreio', 'Processos específicos', 'Controle de etapas'] }
  ];

  return (
    <div>
      <PageHero
        eyebrow="Zartoo ERP"
        title="ERP completo para distribuição e varejo."
        subtitle="Integre compras, vendas, estoque, entregas, fiscal, finanças e BI em um único fluxo."
        primaryCta={{ label: 'Agendar demonstração', href: '/demonstracao' }}
        secondaryCta={{ label: 'Falar com especialista', href: '/especialista' }}
      />

      <section className="bg-slate-950">
        <div className="container mx-auto px-8 lg:px-12 py-16">
          <SectionTitle
            eyebrow="Visão geral"
            title="Gestão ponta a ponta com dados em tempo real"
            description="O Zartoo ERP centraliza operações críticas e elimina retrabalho com automação fiscal, WMS e BI nativo."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {['Integração total', 'IA aplicada', 'Conformidade fiscal'].map((item) => (
              <Card key={item}>
                <h3 className="text-xl font-semibold">{item}</h3>
                <p className="text-slate-200/80 mt-2">
                  Operações conectadas com dashboards instantâneos e rastreabilidade em toda a cadeia.
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900/80">
        <div className="container mx-auto px-8 lg:px-12 py-16">
          <SectionTitle
            eyebrow="Problemas que resolve"
            title="Controle total sem sistemas fragmentados"
            description="Reduza falhas operacionais, ganhos de produtividade e decisões mais rápidas."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {['Erros manuais e retrabalho', 'Falta de visibilidade financeira', 'Rupturas de estoque'].map((item) => (
              <Card key={item}>
                <h3 className="text-xl font-semibold">{item}</h3>
                <p className="text-slate-200/80 mt-2">Processos automatizados e alertas inteligentes para ação imediata.</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950">
        <div className="container mx-auto px-8 lg:px-12 py-16">
          <SectionTitle
            eyebrow="Módulos integrados"
            title="Tudo o que seu negócio precisa em um único ERP"
            description="Módulos conectados para atender operações complexas com velocidade."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((module) => (
              <Card key={module.title}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <module.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-semibold">{module.title}</h3>
                </div>
                <ul className="space-y-1 text-sm text-slate-200/80">
                  {module.items.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#FF6B35]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white text-slate-900">
        <div className="container mx-auto px-8 lg:px-12 py-16">
          <SectionTitle
            eyebrow="Diferenciais"
            title="Integração total e inteligência aplicada"
            description="Geo-roteirização, BI integrado e automações que escalam sua operação."
            mode="light"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {['Geo-roteirização com IA', 'BI nativo em tempo real', 'Rotinas WMS integradas'].map((item) => (
              <Card key={item} variant="light">
                <ShieldCheck className="w-6 h-6 text-[#1976D2]" />
                <h3 className="text-xl font-semibold mt-3">{item}</h3>
                <p className="text-slate-600 mt-2">Tecnologia pensada para operação crítica e tomada de decisão.</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950">
        <div className="container mx-auto px-8 lg:px-12 py-16 text-center">
          <SectionTitle
            eyebrow="CTA"
            title="Pronto para ver o Zartoo ERP em ação?"
            description="Agende uma demonstração e receba um diagnóstico personalizado."
          />
          <a
            href="/demonstracao"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-black text-lg bg-[#FF6B35] text-white hover:shadow-orange-500/40 transition-all"
          >
            Solicitar demonstração
          </a>
        </div>
      </section>
    </div>
  );
};

export default ErpPage;
